import { useEffect, useMemo, useRef, useState } from "react";
import { SearchPanel } from "./components/SearchPanel";
import { ResultsList } from "./components/ResultsList";
import { OriginalText } from "./components/OriginalText";
import { Matrix } from "./components/Matrix";
import { HeatMap, type SelectedHit } from "./components/HeatMap";
import { Footer } from "./components/Footer";
import { useSearchWorker } from "./hooks/useSearchWorker";
import { useTorahText } from "./hooks/useTorahText";

interface PermalinkState {
  wordsText: string;
  dMin: number;
  dMax: number;
  backward: boolean;
}

function readPermalink(): Partial<PermalinkState> {
  if (typeof window === "undefined") return {};
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return {};
  try {
    const params = new URLSearchParams(hash);
    return {
      // The new "ws" key holds the full word list, newline-separated.
      wordsText: params.get("ws") ?? params.get("w") ?? undefined,
      dMin: params.has("dmin") ? parseInt(params.get("dmin")!, 10) : undefined,
      dMax: params.has("dmax") ? parseInt(params.get("dmax")!, 10) : undefined,
      backward: params.has("b") ? params.get("b") === "1" : undefined,
    };
  } catch {
    return {};
  }
}

function writePermalink(state: PermalinkState): void {
  const params = new URLSearchParams();
  if (state.wordsText) params.set("ws", state.wordsText);
  params.set("dmin", String(state.dMin));
  params.set("dmax", String(state.dMax));
  params.set("b", state.backward ? "1" : "0");
  const next = `#${params.toString()}`;
  if (window.location.hash !== next) {
    history.replaceState(null, "", next);
  }
}

// Real Hebrew search words are typically 2–8 letters; up to ~15 covers
// most compound forms with prefixes. Anything longer is almost
// certainly pasted prose (entire Torah passages, sentences, etc.) and
// should not be silently searched — that would balloon the legend,
// stall the worker, and bury the user's actual queries.
const MIN_WORD_LEN = 2;
const MAX_WORD_LEN = 15;

export interface ParsedInput {
  words: string[];
  // Tokens that were dropped, so the UI can explain itself instead of
  // silently ignoring them.
  rejected: Array<{ token: string; reason: "too-short" | "too-long" | "no-hebrew" }>;
}

function stripToHebrew(token: string): string {
  let out = "";
  for (let i = 0; i < token.length; i++) {
    const cc = token.charCodeAt(i);
    // Base Hebrew block 0x05D0..0x05EA (alef..tav) plus the 5 finals
    // 0x05DA, 0x05DD, 0x05DF, 0x05E3, 0x05E5. Niqqud and ta'amim are
    // dropped by virtue of falling outside this range.
    if (
      (cc >= 0x05d0 && cc <= 0x05ea) ||
      cc === 0x05da ||
      cc === 0x05dd ||
      cc === 0x05df ||
      cc === 0x05e3 ||
      cc === 0x05e5
    ) {
      out += token[i];
    }
  }
  return out;
}

export function parseWordsDetailed(text: string): ParsedInput {
  // Split on any run of whitespace (newlines, tabs, spaces, NBSP, etc.).
  // Hebrew words never contain whitespace internally, so this is safe even
  // when users paste tab- or space-separated lists rather than one-per-line.
  const tokens = text.split(/\s+/u).map((t) => t.trim()).filter((t) => t.length > 0);
  const seen = new Set<string>();
  const words: string[] = [];
  const rejected: ParsedInput["rejected"] = [];
  for (const raw of tokens) {
    const heb = stripToHebrew(raw);
    if (heb.length === 0) {
      rejected.push({ token: raw, reason: "no-hebrew" });
      continue;
    }
    if (heb.length < MIN_WORD_LEN) {
      rejected.push({ token: raw, reason: "too-short" });
      continue;
    }
    if (heb.length > MAX_WORD_LEN) {
      rejected.push({ token: raw, reason: "too-long" });
      continue;
    }
    if (seen.has(heb)) continue;
    seen.add(heb);
    words.push(heb);
  }
  return { words, rejected };
}

// Mobile-first tab identifiers. Desktop ignores activeTab and shows
// every pane simultaneously, so this state only matters at narrow
// viewports.
type Tab = "search" | "heatmap" | "matrix" | "text";

const TABS: Array<{ id: Tab; label: string; sub?: string }> = [
  { id: "search", label: "חיפוש", sub: "search" },
  { id: "heatmap", label: "מפה", sub: "heatmap" },
  { id: "matrix", label: "מטריצה", sub: "matrix" },
  { id: "text", label: "תורה", sub: "text" },
];

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia("(max-width: 760px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 760px)");
    const update = () => setMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  return mobile;
}

export function App(): JSX.Element {
  const initial = useMemo(() => readPermalink(), []);

  const [wordsText, setWordsText] = useState<string>(initial.wordsText ?? "");
  // dMin defaults to 2 so we skip the trivial d=1 (consecutive letters,
  // i.e. plain text); users almost never want to see those.
  const [dMin, setDMin] = useState<number>(initial.dMin ?? 2);
  const [dMax, setDMax] = useState<number>(initial.dMax ?? 100);
  const [backward, setBackward] = useState<boolean>(initial.backward ?? true);
  const [selected, setSelected] = useState<SelectedHit | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("search");
  const isMobile = useIsMobile();
  // Word-index mute set; click a legend swatch to toggle. Reset whenever
  // the active word list shrinks (so a stale muted index can't outlive its
  // word).
  const [mutedWords, setMutedWords] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const toggleMuteWord = (i: number) => {
    setMutedWords((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const {
    ready,
    N,
    verses,
    searchResults,
    searchElapsedMs,
    clusterResult,
    searching,
    error: searchError,
    multiSearch,
    cluster,
  } = useSearchWorker();
  const { text, error: textError } = useTorahText();

  const renderStart = performance.now();
  const [lastRenderMs, setLastRenderMs] = useState<number | null>(null);
  const lastRenderStartRef = useRef<number>(renderStart);
  lastRenderStartRef.current = renderStart;

  useEffect(() => {
    setLastRenderMs(performance.now() - lastRenderStartRef.current);
  });

  // Memoize the parsed words list so the search effect only refires when
  // the actual word set changes (not on every keystroke that adds whitespace).
  const parsed = useMemo(() => parseWordsDetailed(wordsText), [wordsText]);
  const words = parsed.words;
  const wordsKey = useMemo(() => words.join("\n"), [words]);

  // Drive multi-search whenever inputs change.
  useEffect(() => {
    if (!ready) return;
    if (words.length === 0) return;
    multiSearch({ words, dMin, dMax, backward });
    // Use the wordsKey to bust the effect when the canonical word list
    // changes; passing `words` directly would refire on every render
    // because parseWords returns a fresh array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, wordsKey, dMin, dMax, backward, multiSearch]);

  // Reset selection when results structure changes meaningfully.
  useEffect(() => {
    if (!selected) return;
    const word = searchResults[selected.wordIndex];
    if (!word || selected.hitIndex >= word.hits.length) {
      setSelected(null);
    }
  }, [searchResults, selected]);

  // Drop muted indices that no longer point to a real word.
  useEffect(() => {
    setMutedWords((prev) => {
      let changed = false;
      const next = new Set<number>();
      for (const i of prev) {
        if (i < searchResults.length) next.add(i);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [searchResults.length]);

  // Drive the cluster scan when selection or word list changes.
  useEffect(() => {
    if (!ready) return;
    if (!selected) return;
    const word = searchResults[selected.wordIndex];
    if (!word) return;
    const hit = word.hits[selected.hitIndex];
    if (!hit) return;
    cluster({
      hit,
      rowsAbove: 12,
      rowsBelow: 12,
      words,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, selected, searchResults, wordsKey, cluster]);

  useEffect(() => {
    writePermalink({ wordsText, dMin, dMax, backward });
  }, [wordsText, dMin, dMax, backward]);

  // When the user selects a heatmap cell on mobile, hop straight to the
  // matrix view — they almost certainly want to inspect the cluster.
  useEffect(() => {
    if (!isMobile || !selected) return;
    setActiveTab("matrix");
  }, [isMobile, selected]);

  // Class hooks let CSS hide non-active panes on mobile while keeping all
  // panes mounted so their workers / scroll positions stay warm.
  const tabClass = (id: Tab) =>
    `pane pane-${id} ${activeTab === id ? "is-active" : ""}`;

  return (
    <div className="app" data-tab={activeTab}>
      <header className="app-header" role="banner">
        <div className="app-header-brand">
          <span className="app-header-mark" lang="he" aria-hidden>
            ד
          </span>
          <span className="app-header-title">
            <span className="brand-he" lang="he">דילוגים</span>
            <span className="brand-en">Torah code search</span>
          </span>
        </div>
        <nav className="app-tabs" role="tablist" aria-label="View">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeTab === t.id}
              className={`app-tab ${activeTab === t.id ? "is-active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              <span className="app-tab-he" lang="he">{t.label}</span>
              {t.sub && <span className="app-tab-sub">{t.sub}</span>}
            </button>
          ))}
        </nav>
      </header>

      <div className="app-body">
        <aside className={tabClass("search")} aria-label="Search controls">
          <SearchPanel
            wordsText={wordsText}
            setWordsText={setWordsText}
            dMin={dMin}
            setDMin={setDMin}
            dMax={dMax}
            setDMax={setDMax}
            backward={backward}
            setBackward={setBackward}
            ready={ready}
            searching={searching}
            results={searchResults}
            searchMs={searchElapsedMs > 0 ? searchElapsedMs : null}
            renderMs={lastRenderMs}
            mutedWords={mutedWords}
            toggleMuteWord={toggleMuteWord}
            acceptedWords={words}
            rejected={parsed.rejected}
          />
          <ResultsList
            results={searchResults}
            verses={verses}
            selected={selected}
            onSelect={(s) => {
              setSelected(s);
              if (isMobile) setActiveTab("matrix");
            }}
          />
          {(searchError || textError) && (
            <div className="error-banner" role="alert">
              {searchError ?? textError}
            </div>
          )}
        </aside>

        <main className="main-pane">
          <section className={tabClass("heatmap")} aria-label="Heatmap">
            <h2 className="pane-title">
              <span className="pane-title-he" lang="he">מפת חום</span>
              <span className="pane-title-en">heatmap · skip × position</span>
            </h2>
            <HeatMap
              results={searchResults}
              N={N}
              dMin={dMin}
              dMax={dMax}
              selected={selected}
              onSelect={setSelected}
              verses={verses}
              mutedWords={mutedWords}
            />
          </section>
          <section className={tabClass("matrix")} aria-label="Matrix">
            <h2 className="pane-title">
              <span className="pane-title-he" lang="he">מטריצה</span>
              <span className="pane-title-en">matrix</span>
            </h2>
            <Matrix
              cluster={clusterResult}
              selected={selected}
              mutedWords={mutedWords}
              results={searchResults}
              dMin={dMin}
              dMax={dMax}
            />
          </section>
          <section className={tabClass("text")} aria-label="Torah text">
            <h2 className="pane-title">
              <span className="pane-title-he" lang="he">טקסט התורה</span>
              <span className="pane-title-en">torah text</span>
            </h2>
            <OriginalText
              text={text?.originalText ?? ""}
              posmap={text?.posmap ?? null}
              verses={verses}
              results={searchResults}
              selected={selected}
              mutedWords={mutedWords}
            />
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
}
